import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// src/features/time_serie_files/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  CheckboxField,
  DatePickerField,
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import {
  dateSchema,
  parseToDateValue,
  parseYes,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import { statusOptions } from "./filter";

const statusSelectOptions = statusOptions.map((opt, idx) => ({
  value: idx.toString(),
  label: opt.label,
}));

const getStatusOptionValue = (
  backendStatus: number | null | undefined,
): string => {
  if (backendStatus === null || backendStatus === undefined) return "";
  return backendStatus.toString();
};

const languageOptions = [
  { value: "EN", label: "EN" },
  { value: "FR", label: "FR" },
];

const doctypeOptions = [
  { value: "Factsheet", label: "Factsheet" },
  { value: "KID", label: "KID" },
];

const FormSchema = z.object({
  file_file_name: z.string(),
  source_folder: z.string().nullable().optional(),
  destination: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  input_file_id: z.coerce.number().optional().nullable(),
  status: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce.number().optional().nullable(),
  ),
  is_manually_uploaded: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  provider_config_code: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string().nullable().optional(),
        }),
      ])
      .optional(),
  ),
  provider_config_id: z.coerce.number().optional().nullable(),

  owner_id: z.coerce.number().optional().nullable(),
  language: z.string().optional().nullable(),
  doctype: z.string().optional().nullable(),
  closing_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  error_messages: z.preprocess((val: unknown) => {
    // Let empty values pass through
    if (val === null || val === undefined || val === "") return null;

    // If it's already a string, keep it
    if (typeof val === "string") return val;

    // If it's JSON (array/object/number/bool), pretty stringify for the textarea
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      // fallback to string coercion
      return String(val);
    }
  }, z.string().nullable().optional()),
});

export function TimeSerieFilesEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit: originalOnSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "provider_config_code") {
        return {
          id: fetchedData.provider_config_id,
          name: fetchedData.provider_config_code,
          text: fetchedData.provider_config_code,
        };
      }
      if (key === "status") {
        return getStatusOptionValue(value);
      }
      return value;
    },
  });

  const onSubmit = (data: any) => {
    return originalOnSubmit({
      owner_id: data.owner_id ?? null,
      language: data.language ?? null,
      doctype: data.doctype ?? null,
      closing_date: data.closing_date ?? null,
      status: data.status ?? null,
    });
  };

  const configId = form.watch("provider_config_id");

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                TimeSerieFiles {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-6">
                <InputField
                  name="file_file_name"
                  label={t(`${table_name}.columns.file_file_name`, {
                    ns: "tables",
                    defaultValue: "File Name",
                  })}
                  type="text"
                  control={form.control}
                  fieldOptions={{ isDisabled: true }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="provider_config_code"
                    label={t(`${table_name}.columns.provider_config_code`, {
                      ns: "tables",
                      defaultValue: "Provider Config",
                    })}
                    base_url="etl-quantilia/provider_configs"
                    column_name="code"
                    id_column="provider_config_id"
                    control={form.control}
                    options={{
                      itemLink: toWorkspacePath(
                        `/admin/qtl - etl/provider_configs/${configId}`,
                      ),
                    }}
                  />
                  <SelectField
                    name="status"
                    label={t(`${table_name}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    options={statusSelectOptions}
                    control={form.control}
                  />
                </div>

                {/* PDF section */}
                <Divider />
                <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  PDF
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="owner_id"
                    label={t(`${table_name}.columns.owner_id`, {
                      ns: "tables",
                      defaultValue: "Owner ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <SelectField
                    name="language"
                    label={t(`${table_name}.columns.language`, {
                      ns: "tables",
                      defaultValue: "Language",
                    })}
                    options={languageOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="doctype"
                    label={t(`${table_name}.columns.doctype`, {
                      ns: "tables",
                      defaultValue: "Doctype",
                    })}
                    options={doctypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <DatePickerField
                    name="closing_date"
                    label={t(`${table_name}.columns.closing_date`, {
                      ns: "tables",
                      defaultValue: "Reference date",
                    })}
                    control={form.control}
                  />
                </div>
                <Divider />

                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="error_messages"
                    label={t(`${table_name}.columns.error_messages`, {
                      ns: "tables",
                      defaultValue: "Error messages (JSON Array)",
                    })}
                    control={form.control}
                    fieldOptions={{ isReadOnly: true }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="input_file_id"
                    label={t(`${table_name}.columns.input_file_id`, {
                      ns: "tables",
                      defaultValue: "Input File ID",
                    })}
                    type="number"
                    control={form.control}
                    fieldOptions={{ changeOnDisable: false, isDisabled: true }}
                  />
                  <div className="self-end flex items-center">
                    <CheckboxField
                      name="is_manually_uploaded"
                      label={t(`${table_name}.columns.is_manually_uploaded`, {
                        ns: "tables",
                        defaultValue: "Manual Upload",
                      })}
                      control={form.control}
                      fieldOptions={{ isDisabled: true }}
                    />
                  </div>
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["provider_config_id", "input_file_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
